import React, { useState, useRef } from 'react';
import './PDFUpload.css';
import apiClient from '../../services/apiClient';

const PDFUpload = ({ value, onChange, label = "Télécharger le CV (PDF)" }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFile = async (file) => {
    // Validate file type
    if (!file.type.includes('pdf')) {
      alert('⚠️ Veuillez sélectionner un fichier PDF uniquement.');
      return;
    }

    // Check file size (max 10MB for PDFs)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert(`⚠️ Fichier trop volumineux!\n\nTaille actuelle: ${(file.size / 1024 / 1024).toFixed(2)}MB\nTaille maximale: 10MB`);
      return;
    }

    setUploading(true);
    
    try {
      console.log('📄 Uploading PDF to backend:', file.name);
      
      // Get token from localStorage
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Non authentifié. Veuillez vous reconnecter.');
      }

      // Create FormData
      const formData = new FormData();
      formData.append('files', file);

      // Upload to backend
      const response = await fetch(`${apiClient.baseURL}/media/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ PDF upload successful:', result);

      // Get the URL from the response
      let pdfUrl = '';
      if (result.files && result.files.length > 0) {
        const fileObj = result.files[0];
        // Prioritize public_url
        if (fileObj.public_url) {
          pdfUrl = fileObj.public_url;
        } else if (fileObj.file_url) {
          // Construct full URL
          pdfUrl = fileObj.file_url.startsWith('http') 
            ? fileObj.file_url 
            : `https://dentoxcellence-backend.boufaamoundir.workers.dev${fileObj.file_url}`;
        }
      }

      if (pdfUrl) {
        onChange(pdfUrl);
        alert('✅ PDF téléchargé avec succès!');
      } else {
        throw new Error('No URL returned from upload');
      }

    } catch (error) {
      console.error('❌ Error uploading PDF:', error);
      alert(`❌ Erreur lors du téléchargement: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce CV ?')) {
      onChange('');
    }
  };

  const getFileName = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const parts = pathname.split('/');
      return decodeURIComponent(parts[parts.length - 1]);
    } catch {
      return 'cv.pdf';
    }
  };

  return (
    <div className="pdf-upload-container">
      {value ? (
        <div className="pdf-preview">
          <div className="pdf-preview-header">
            <div className="pdf-icon-wrapper">
              <i className="fas fa-file-pdf"></i>
            </div>
            <div className="pdf-info">
              <span className="pdf-filename">{getFileName(value)}</span>
              <span className="pdf-status">✓ CV téléchargé</span>
            </div>
            <button
              type="button"
              className="btn-remove-pdf"
              onClick={handleRemove}
              title="Supprimer le PDF"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          <div className="pdf-actions">
            <button
              type="button"
              onClick={async () => {
                try {
                  // Fetch the PDF
                  const response = await fetch(value);
                  const blob = await response.blob();
                  
                  // Create blob URL
                  const blobUrl = URL.createObjectURL(blob);
                  
                  // Open in new tab
                  window.open(blobUrl, '_blank');
                  
                  // Clean up blob URL after a delay
                  setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
                } catch (error) {
                  console.error('Error loading PDF:', error);
                  alert('Erreur lors du chargement du PDF');
                }
              }}
              className="btn-view-pdf"
            >
              <i className="fas fa-eye"></i>
              Prévisualiser le PDF
            </button>
          </div>
        </div>
      ) : (
        <>
          <div
            className={`pdf-drop-zone ${isDragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="drop-zone-content">
              {uploading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>Téléchargement en cours...</p>
                </>
              ) : (
                <>
                  <i className="fas fa-file-pdf"></i>
                  <p>{label}</p>
                  <p className="drop-zone-hint">Glissez-déposez un PDF ou cliquez pour parcourir</p>
                  <div className="file-requirements">
                    <span>Format PDF uniquement</span>
                    <span>•</span>
                    <span style={{color: '#10b981', fontWeight: 'bold'}}>Max 10MB</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf,application/pdf"
            style={{ display: 'none' }}
          />
        </>
      )}
    </div>
  );
};

export default PDFUpload;

